import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import './AgencyReportsAnalytics.css';
import api from '../../config/api';
import * as tripsService from '../../services/tripsService';
import * as vehiclesService from '../../services/vehiclesService';
import * as driversService from '../../services/driversService';

const AgencyReportsAnalytics = () => {
  // Sub-tabs state
  const [activeReportTab, setActiveReportTab] = useState('Trip Report');
  
  // Report generation state
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Table state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const showEntriesDropdownRef = useRef(null);
  
  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [vehicleFilter, setVehicleFilter] = useState('All');
  const [driverFilter, setDriverFilter] = useState('All');
  const [billingTypeFilter, setBillingTypeFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Agency context
  const [currentAgencyId, setCurrentAgencyId] = useState(null);

  useEffect(() => {
    const savedAgency = localStorage.getItem('agencyProfile');
    if (savedAgency) {
      try {
        const agency = JSON.parse(savedAgency);
        const agencyId = agency.id || agency.agency_id || null;
        setCurrentAgencyId(agencyId);
        console.log('Agency Reports: Loaded agency ID:', agencyId, 'Agency Name:', agency.name);
      } catch (error) {
        console.error('Agency Reports: Error loading agency profile:', error);
      }
    }
  }, []);

  // Trips data from API (for current agency only)
  const [trips, setTrips] = useState([]);
  const [tripsTotalCount, setTripsTotalCount] = useState(0);
  const [loadingTrips, setLoadingTrips] = useState(false);

  // Bills data from API (for current agency only)
  const [bills, setBills] = useState([]);
  const [billsTotalCount, setBillsTotalCount] = useState(0);
  const [loadingBills, setLoadingBills] = useState(false);

  // Vehicles and Drivers for dropdowns (for current agency only)
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loadingFilterData, setLoadingFilterData] = useState(false);

  const fetchBills = async () => {
    if (activeReportTab !== 'Billing Report') return;
    if (!currentAgencyId) {
      console.warn('Agency Reports: No agency ID found, cannot load bills');
      return;
    }

    setLoadingBills(true);
    try {
      const params = new URLSearchParams();

      // Always restrict to current agency
      params.append('agency_id', currentAgencyId);

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      if (statusFilter && statusFilter !== 'All') {
        params.append('payment_status', statusFilter);
      }

      if (fromDate) {
        params.append('start_date', fromDate);
      }
      if (toDate) {
        params.append('end_date', toDate);
      }

      // Pagination
      params.append('limit', itemsPerPage);
      params.append('offset', (currentPage - 1) * itemsPerPage);
      params.append('order_by', 'bill_date');
      params.append('sort_order', 'desc');

      const response = await api.get('/api/bills', { params: Object.fromEntries(params.entries()) });
      const json = response.data;

      if (json && json.success && json.data && Array.isArray(json.data.items)) {
        const normalizedBills = json.data.items.map(b => ({
          id: b.bill_number || b.id || '',
          billDate: b.bill_date || '',
          billDateFormatted: b.bill_date
            ? new Date(b.bill_date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })
            : '',
          fromLocation: b.from_location || '',
          toLocation: b.to_location || '',
          vehicleNumber: b.vehicle_number || '',
          driverName: b.driver_name || '',
          totalAmount: typeof b.total_amount !== 'undefined' ? Number(b.total_amount) : 0,
          status: b.payment_status || 'Pending',
          raw: b
        }));

        setBills(normalizedBills);
        setBillsTotalCount(json.data.total || json.data.count || normalizedBills.length);
      } else {
        setBills([]);
        setBillsTotalCount(0);
      }
    } catch (err) {
      console.error('Agency Reports: Failed to fetch bills:', err);
      setBills([]);
      setBillsTotalCount(0);
    } finally {
      setLoadingBills(false);
    }
  };

  // Get unique vehicles and drivers for dropdowns (from current agency only)
  const uniqueVehicles = useMemo(() => {
    const vehicleList = ['All'];
    vehicles.forEach(v => {
      const vehicleNumber = v.vehicle_number || v.number || '';
      if (vehicleNumber && !vehicleList.includes(vehicleNumber)) {
        vehicleList.push(vehicleNumber);
      }
    });
    return vehicleList;
  }, [vehicles]);

  const uniqueDrivers = useMemo(() => {
    const driverList = ['All'];
    console.log('Agency Reports: Building unique drivers list from', drivers.length, 'drivers');
    drivers.forEach(d => {
      // Try multiple possible field names for driver name
      const driverName = d.full_name || d.name || d.driver_name || d.fullName || '';
      if (driverName && !driverList.includes(driverName)) {
        driverList.push(driverName);
        console.log('Agency Reports: Added driver to list:', driverName);
      } else if (!driverName) {
        console.warn('Agency Reports: Driver has no name:', d);
      }
    });
    console.log('Agency Reports: Final unique drivers list:', driverList);
    return driverList;
  }, [drivers]);

  // Map trip from API to component format
  const mapTripFromAPI = useCallback((apiTrip) => {
    // Extract vehicle number
    const vehicleNumber = apiTrip.vehicle?.vehicle_number || 
                         apiTrip.vehicle_number || 
                         (apiTrip.vehicle_id ? `Vehicle #${apiTrip.vehicle_id}` : 'N/A');

    // Extract driver name
    const driverName = apiTrip.driver?.full_name || 
                      apiTrip.driver_name || 
                      apiTrip.assigned_driver?.full_name ||
                      (apiTrip.assigned_driver_id ? `Driver #${apiTrip.assigned_driver_id}` : 'Not Assigned');

    // Build route
    const route = apiTrip.start_location && apiTrip.destination
      ? `${apiTrip.start_location} → ${apiTrip.destination}`
      : apiTrip.route || 'N/A';

    // Format date
    let date = '';
    if (apiTrip.trip_date_time) {
      const tripDate = new Date(apiTrip.trip_date_time);
      if (!isNaN(tripDate.getTime())) {
        date = tripDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      }
    }

    // Get distance
    const distanceKm = apiTrip.estimated_distance || apiTrip.actual_distance || 0;

    return {
      id: apiTrip.trip_id || apiTrip.id || '',
      date: date,
      vehicleNumber: vehicleNumber,
      driverName: driverName,
      route: route,
      status: apiTrip.trip_status || apiTrip.status || 'Scheduled',
      distanceKm: distanceKm,
      _original: apiTrip
    };
  }, []);

  // Fetch vehicles for dropdown (for current agency only)
  const fetchVehicles = useCallback(async () => {
    if (!currentAgencyId) return;
    
    try {
      const response = await vehiclesService.getVehicles({ page: 1, limit: 1000, agency_id: currentAgencyId });
      let vehiclesData = [];
      if (response?.data && Array.isArray(response.data)) {
        vehiclesData = response.data;
      } else if (response?.data?.items && Array.isArray(response.data.items)) {
        vehiclesData = response.data.items;
      } else if (Array.isArray(response)) {
        vehiclesData = response;
      } else {
        vehiclesData = response?.data?.items || response?.items || [];
      }
      setVehicles(vehiclesData);
    } catch (err) {
      console.error('Agency Reports: Error fetching vehicles:', err);
      setVehicles([]);
    }
  }, [currentAgencyId]);

  // Fetch drivers for dropdown (for current agency only)
  const fetchDrivers = useCallback(async () => {
    if (!currentAgencyId) {
      console.log('Agency Reports: No agency ID, skipping driver fetch');
      return;
    }
    
    let response;
    let usedFallback = false;
    
    try {
      console.log('Agency Reports: Fetching drivers for agency ID:', currentAgencyId);
      response = await driversService.getDrivers({ page: 1, limit: 1000, agency_id: currentAgencyId });
      console.log('Agency Reports: Drivers API response:', response);
    } catch (apiError) {
      // If API fails with agency_id filter, try fetching all drivers and filter client-side
      const is500Error = apiError.response?.status === 500 || 
                        (apiError.message && apiError.message.includes('500')) ||
                        (apiError.message && apiError.message.includes('Internal Server Error'));
      
      if (currentAgencyId && is500Error) {
        console.warn('Agency Reports: API returned 500 error with agency_id filter.');
        console.warn('Trying fallback: Fetching all drivers without agency_id filter, then filtering client-side...');
        
        const fallbackParams = { page: 1, limit: 1000 };
        // Don't include agency_id in fallback call
        
        try {
          response = await driversService.getDrivers(fallbackParams);
          usedFallback = true;
          console.log('Agency Reports: Fallback API call succeeded, will filter client-side');
        } catch (fallbackError) {
          console.error('Agency Reports: Fallback API call also failed:', fallbackError);
          setDrivers([]);
          return;
        }
      } else {
        console.error('Agency Reports: Error fetching drivers:', apiError);
        console.error('Agency Reports: Error details:', apiError.response?.data || apiError.message);
        setDrivers([]);
        return;
      }
    }
    
    try {
      let driversData = [];
      
      // Handle response structure - API returns { success, message, data, pagination }
      // where data is an array of drivers (similar to AgencyTripManagement)
      if (response) {
        // API response structure: { success, message, data, pagination }
        // where data is an array of drivers
        if (Array.isArray(response.data)) {
          driversData = response.data;
        } else if (response.data && Array.isArray(response.data.items)) {
          driversData = response.data.items;
        } else if (Array.isArray(response)) {
          driversData = response;
        } else {
          // Try nested structures
          if (response.data?.data && Array.isArray(response.data.data)) {
            driversData = response.data.data;
          } else if (response.data?.data?.items && Array.isArray(response.data.data.items)) {
            driversData = response.data.data.items;
          } else {
            driversData = response.data?.items || response.items || [];
          }
        }
      }
      
      // Ensure driversData is an array
      if (!Array.isArray(driversData)) {
        console.warn('Agency Reports: driversData is not an array:', driversData);
        driversData = [];
      }
      
      // If we used fallback, filter by agency_id client-side
      if (usedFallback && currentAgencyId) {
        console.log('Agency Reports: Filtering', driversData.length, 'drivers by agency_id:', currentAgencyId);
        driversData = driversData.filter(driver => {
          const driverAgencyId = driver.agency_id || driver.agency?.id || driver.agency?.agency_id;
          return driverAgencyId && Number(driverAgencyId) === Number(currentAgencyId);
        });
        console.log('Agency Reports: After filtering,', driversData.length, 'drivers remain');
      }
      
      console.log('Agency Reports: Extracted drivers data:', driversData.length, 'drivers');
      if (driversData.length > 0) {
        console.log('Agency Reports: Sample driver:', driversData[0]);
        console.log('Agency Reports: Driver name fields:', {
          full_name: driversData[0].full_name,
          name: driversData[0].name,
          driver_name: driversData[0].driver_name
        });
      } else {
        console.warn('Agency Reports: No drivers found in response');
      }
      
      setDrivers(driversData);
    } catch (err) {
      console.error('Agency Reports: Error processing drivers data:', err);
      setDrivers([]);
    }
  }, [currentAgencyId]);

  // Fetch trips from API (for current agency only)
  const fetchTrips = useCallback(async () => {
    if (activeReportTab !== 'Trip Report') return;
    if (!currentAgencyId) {
      console.warn('Agency Reports: No agency ID found, cannot load trips');
      return;
    }
    
    setLoadingTrips(true);
    try {
      const params = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        agency_id: currentAgencyId, // Always filter by current agency
      };

      // Add search query
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add status filter
      if (statusFilter && statusFilter !== 'All') {
        params.trip_status = statusFilter;
      }

      // Add vehicle filter
      if (vehicleFilter && vehicleFilter !== 'All') {
        const vehicle = vehicles.find(v => {
          const vehicleNumber = v.vehicle_number || v.number || '';
          return vehicleNumber === vehicleFilter;
        });
        if (vehicle && vehicle.id) {
          params.vehicle_id = vehicle.id;
        }
      }

      // Add driver filter
      if (driverFilter && driverFilter !== 'All') {
        const driver = drivers.find(d => {
          const driverName = d.full_name || d.name || '';
          return driverName === driverFilter;
        });
        if (driver && driver.id) {
          params.assigned_driver_id = driver.id;
        }
      }

      // Add date filters
      if (fromDate) {
        params.start_date = new Date(fromDate).toISOString();
      }
      if (toDate) {
        // Set to end of day for toDate
        const endDate = new Date(toDate);
        endDate.setHours(23, 59, 59, 999);
        params.end_date = endDate.toISOString();
      }

      const response = await tripsService.getTrips(params);
      
      // Handle response structure
      let tripsData = [];
      let total = 0;
      
      if (response?.data) {
        tripsData = response.data.items || response.data.trips || [];
        total = response.data.total || response.data.count || 0;
      } else {
        tripsData = response.items || response.trips || [];
        total = response.total || response.count || 0;
      }

      // Map API trips to component format
      const mappedTrips = Array.isArray(tripsData) 
        ? tripsData.map(trip => mapTripFromAPI(trip))
        : [];

      setTrips(mappedTrips);
      setTripsTotalCount(total);
    } catch (err) {
      console.error('Agency Reports: Failed to fetch trips:', err);
      setTrips([]);
      setTripsTotalCount(0);
    } finally {
      setLoadingTrips(false);
    }
  }, [activeReportTab, currentAgencyId, searchQuery, statusFilter, vehicleFilter, driverFilter, fromDate, toDate, currentPage, itemsPerPage, vehicles, drivers, mapTripFromAPI]);

  // Fetch vehicles and drivers when agency ID is available
  useEffect(() => {
    if (currentAgencyId) {
      fetchVehicles();
      fetchDrivers();
    }
  }, [currentAgencyId, fetchVehicles, fetchDrivers]);

  // Fetch trips when filters change or when report is generated
  useEffect(() => {
    if (activeReportTab === 'Trip Report' && reportGenerated && currentAgencyId) {
      fetchTrips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReportTab, reportGenerated, currentPage, itemsPerPage, searchQuery, statusFilter, vehicleFilter, driverFilter, fromDate, toDate, currentAgencyId]);

  // Filter data - For Trip Report, data is already filtered by API. For Billing Report, data is already filtered by API
  const filteredData = useMemo(() => {
    if (activeReportTab === 'Trip Report') {
      // Trips are already filtered by API, just return them
      return trips;
    } else if (activeReportTab === 'Billing Report') {
      // Bills are already filtered by API
      return bills;
    }
    return [];
  }, [activeReportTab, trips, bills]);

  // Pagination
  // Both Trip Report and Billing Report pagination are handled by API
  const totalPages = useMemo(() => {
    if (activeReportTab === 'Billing Report') {
      return Math.ceil(billsTotalCount / itemsPerPage) || 1;
    } else if (activeReportTab === 'Trip Report') {
      return Math.ceil(tripsTotalCount / itemsPerPage) || 1;
    }
    return 1;
  }, [billsTotalCount, tripsTotalCount, itemsPerPage, activeReportTab]);
  
  const paginatedData = useMemo(() => {
    // Both reports are already paginated by API
    return filteredData;
  }, [filteredData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // Export functions
  const exportCsv = (data, headers, filename) => {
    const csv = [headers.join(','), ...data.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = () => {
    if (activeReportTab === 'Trip Report') {
      exportCsv(filteredData, ['id', 'date', 'vehicleNumber', 'driverName', 'route', 'status', 'distanceKm'], 'trip-report.csv');
    } else if (activeReportTab === 'Billing Report') {
      exportCsv(filteredData, ['id', 'billDate', 'fromLocation', 'toLocation', 'vehicleNumber', 'driverName', 'totalAmount', 'status'], 'billing-report.csv');
    }
  };

  const handlePrintReport = () => {
    // Print all filtered bills in a report format
    if (activeReportTab === 'Billing Report' && filteredData.length > 0) {
      printBillingReport(filteredData);
    }
  };

  const handlePrintBill = (bill) => {
    // Print individual bill similar to BillGeneration
    printSingleBill(bill);
  };

  const printSingleBill = (bill) => {
    // Format date
    const billDate = formatDate(bill.billDate);
    
    // Create HTML content for bill print matching the carrier rate confirmation template
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Carrier's Rate Confirmation - ${bill.id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: white;
      color: #333;
      padding: 20px;
    }
    .document-container {
      max-width: 1000px;
      margin: 0 auto;
      background: white;
    }
    .header-section {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 30px;
      padding-bottom: 20px;
    }
    .company-info {
      flex: 1;
    }
    .company-name {
      font-size: 22px;
      font-weight: 700;
      color: #1e3a8a;
      margin-bottom: 8px;
    }
    .company-tagline {
      font-size: 11px;
      color: #6b7280;
      margin-bottom: 8px;
    }
    .company-details {
      font-size: 11px;
      color: #333;
      line-height: 1.8;
    }
    .document-title-section {
      text-align: right;
    }
    .document-title {
      font-size: 18px;
      font-weight: 700;
      color: #1e3a8a;
      margin-bottom: 10px;
      text-transform: uppercase;
    }
    .document-id {
      font-size: 12px;
      color: #333;
      margin-top: 10px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 25px;
    }
    .info-box {
      padding: 15px;
      background: #f9fafb;
      margin-bottom: 20px;
    }
    .info-box-title {
      font-size: 13px;
      font-weight: 700;
      color: #1e3a8a;
      margin-bottom: 12px;
      text-transform: uppercase;
    }
    .info-item {
      font-size: 12px;
      margin-bottom: 8px;
      line-height: 1.6;
    }
    .info-item-label {
      font-weight: 600;
      color: #4b5563;
      display: inline-block;
      min-width: 120px;
    }
    .info-item-value {
      color: #111827;
    }
    .charges-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
      table-layout: fixed;
    }
    .charges-table thead {
      background: #1e3a8a;
      color: white;
    }
    .charges-table th {
      padding: 12px 10px;
      text-align: left;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      border: 1px solid #e5e7eb;
    }
    .charges-table th:nth-child(2) {
      text-align: center;
    }
    .charges-table th:nth-child(3) {
      text-align: right;
    }
    .charges-table td {
      padding: 12px 10px;
      font-size: 13px;
      background: white;
      border: 1px solid #e5e7eb;
    }
    .charges-table td:nth-child(2) {
      text-align: center;
    }
    .charges-table td:nth-child(3) {
      text-align: right;
      font-weight: 500;
    }
    .charges-table tbody tr:nth-child(even) {
      background: #f9fafb;
    }
    .total-row {
      background: #f0f9ff !important;
      font-weight: 700;
    }
    .total-row td {
      padding: 15px 10px;
      font-size: 14px;
    }
    @media print {
      body { margin: 0; padding: 15px; }
      .document-container { max-width: 100%; page-break-inside: avoid; }
      .info-grid, .info-box, .charges-table { page-break-inside: avoid; }
      @page {
        margin: 1cm;
        size: A4;
      }
    }
  </style>
</head>
<body>
  <div class="document-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="company-info">
        <div class="company-name">Nettaa Logistics Service</div>
        <div class="company-tagline">YOUR TRUCKING COMPANY</div>
        <div class="company-details">
          Truck Booking App, Truck Rental, Logistics App<br>
          Packers and Movers, India<br>
          E-mail: info@nettaalogistics.com<br>
          Website: www.nettaalogistics.com
        </div>
      </div>
      <div class="document-title-section">
        <div class="document-title">CARRIER'S RATE CONFIRMATION</div>
        <div class="document-id">Bill ID: ${bill.id}</div>
        <div class="document-id">Date: ${billDate}</div>
      </div>
    </div>

    <!-- Information Grid: Row 1 (Carrier Info) -->
    <div class="info-grid">
      <div>
        <div class="info-box">
          <div class="info-box-title">CARRIER INFORMATION</div>
          <div class="info-item">
            <span class="info-item-label">Vehicle Number:</span>
            <span class="info-item-value">${bill.vehicleNumber || 'Not specified'}</span>
          </div>
          <div class="info-item">
            <span class="info-item-label">Driver Name:</span>
            <span class="info-item-value">${bill.driverName || 'Not specified'}</span>
          </div>
        </div>
      </div>
      <div></div>
    </div>

    <!-- Information Grid: Row 2 (Pickup left, Delivery right) -->
    <div class="info-grid">
      <div>
        <div class="info-box">
          <div class="info-box-title">PICKUP INFORMATION</div>
          <div class="info-item">
            <span class="info-item-label">From Location:</span>
            <span class="info-item-value">${bill.fromLocation || 'Not specified'}</span>
          </div>
        </div>
      </div>
      <div>
        <div class="info-box">
          <div class="info-box-title">DELIVERY INFORMATION</div>
          <div class="info-item">
            <span class="info-item-label">To Location:</span>
            <span class="info-item-value">${bill.toLocation || 'Not specified'}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Charges Table -->
    <table class="charges-table">
      <thead>
        <tr>
          <th style="width: 40%;">CHARGE TYPE</th>
          <th style="width: 30%;">UNIT</th>
          <th style="width: 30%;">AMOUNT</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Transportation Charges</td>
          <td>-</td>
          <td>₹${bill.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr class="total-row">
          <td colspan="2" style="text-align: right; padding-right: 20px;">TOTAL CHARGE</td>
          <td>₹${bill.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>
  </div>
</body>
</html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const printBillingReport = (bills) => {
    // Create HTML content for billing report
    const reportDate = new Date().toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
    
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Billing Report - ${reportDate}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: white;
      color: #333;
      padding: 20px;
    }
    .report-container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
    }
    .report-header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 2px solid #1e3a8a;
    }
    .report-title {
      font-size: 24px;
      font-weight: 700;
      color: #1e3a8a;
      margin-bottom: 10px;
    }
    .report-date {
      font-size: 14px;
      color: #6b7280;
    }
    .report-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
    }
    .report-table thead {
      background: #1e3a8a;
      color: white;
    }
    .report-table th {
      padding: 12px;
      text-align: left;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      border: 1px solid #e5e7eb;
    }
    .report-table th:last-child {
      text-align: right;
    }
    .report-table td {
      padding: 12px;
      font-size: 13px;
      border: 1px solid #e5e7eb;
    }
    .report-table td:last-child {
      text-align: right;
    }
    .report-table tbody tr:nth-child(even) {
      background: #f9fafb;
    }
    .total-row {
      background: #f0f9ff !important;
      font-weight: 700;
    }
    .status-badge {
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
    }
    .status-paid {
      background-color: #d1fae5;
      color: #065f46;
    }
    .status-pending {
      background-color: #e5e7eb;
      color: #374151;
    }
    @media print {
      body { margin: 0; padding: 15px; }
      .report-container { max-width: 100%; }
      @page {
        margin: 1cm;
        size: A4;
      }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="report-header">
      <div class="report-title">Billing Report</div>
      <div class="report-date">Generated on: ${reportDate}</div>
    </div>
    <table class="report-table">
      <thead>
        <tr>
          <th>Invoice Number</th>
          <th>Invoice Date</th>
          <th>From Location</th>
          <th>To Location</th>
          <th>Total Amount (₹)</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        ${bills.map(bill => `
          <tr>
            <td>${bill.id}</td>
            <td>${formatDate(bill.billDate)}</td>
            <td>${bill.fromLocation}</td>
            <td>${bill.toLocation}</td>
            <td>₹${bill.totalAmount.toLocaleString('en-IN')}</td>
            <td>
              <span class="status-badge ${bill.status === 'Paid' ? 'status-paid' : 'status-pending'}">
                ${bill.status}
              </span>
            </td>
          </tr>
        `).join('')}
        <tr class="total-row">
          <td colspan="4" style="text-align: right; padding-right: 20px;">TOTAL</td>
          <td>₹${bills.reduce((sum, b) => sum + b.totalAmount, 0).toLocaleString('en-IN')}</td>
          <td></td>
        </tr>
      </tbody>
    </table>
  </div>
</body>
</html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const handleGenerateReport = async () => {
    if (activeReportTab === 'Billing Report') {
      await fetchBills();
    } else if (activeReportTab === 'Trip Report') {
      await fetchTrips();
    }
    setReportGenerated(true);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setStatusFilter('All');
    setVehicleFilter('All');
    setDriverFilter('All');
    setBillingTypeFilter('All');
    setSearchQuery('');
    setCurrentPage(1);
    setReportGenerated(false);
    if (activeReportTab === 'Billing Report') {
      setBills([]);
      setBillsTotalCount(0);
    } else if (activeReportTab === 'Trip Report') {
      setTrips([]);
      setTripsTotalCount(0);
    }
  };

  // Get table headers based on active tab
  const getTableHeaders = () => {
    if (activeReportTab === 'Trip Report') {
      return ['Trip ID', 'Date', 'Vehicle Number', 'Driver Name', 'Route', 'Distance (km)', 'Status'];
    } else if (activeReportTab === 'Billing Report') {
      return ['Invoice Number', 'Invoice Date', 'From Location', 'To Location', 'Total Amount (₹)', 'Status', 'Actions'];
    }
    return [];
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'completed' || statusLower === 'paid') return 'status-badge-success';
    if (statusLower === 'scheduled' || statusLower === 'pending') return 'status-badge-pending';
    if (statusLower === 'in progress') return 'status-badge-progress';
    if (statusLower === 'delayed') return 'status-badge-warning';
    if (statusLower === 'cancelled' || statusLower === 'unbilled') return 'status-badge-danger';
    return 'status-badge-default';
  };

  // Track previous tab to detect tab changes
  const prevTabRef = useRef(activeReportTab);

  // Reset page when filters change (but not when tab changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, statusFilter, vehicleFilter, driverFilter, billingTypeFilter, searchQuery]);

  // Reset report generated state and data when tab changes
  useEffect(() => {
    if (prevTabRef.current !== activeReportTab) {
      setReportGenerated(false);
      setCurrentPage(1);
      // Reset data when switching tabs
      if (activeReportTab !== 'Billing Report') {
        setBills([]);
        setBillsTotalCount(0);
      }
      if (activeReportTab !== 'Trip Report') {
        setTrips([]);
        setTripsTotalCount(0);
      }
      prevTabRef.current = activeReportTab;
    }
  }, [activeReportTab]);

  // Fetch data when pagination changes and report is generated
  useEffect(() => {
    if (activeReportTab === 'Billing Report' && reportGenerated) {
      fetchBills();
    } else if (activeReportTab === 'Trip Report' && reportGenerated && currentAgencyId) {
      fetchTrips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, activeReportTab, reportGenerated, currentAgencyId]);


  return (
    <div className="reports-analytics-container">
      {/* Header */}
      <div className="ra-header-section">
        <div className="ra-header-actions" />
      </div>

      {/* Sub-tabs */}
      <div className="ra-sub-tabs">
        <button
          className={`ra-sub-tab ${activeReportTab === 'Trip Report' ? 'active' : ''}`}
          onClick={() => setActiveReportTab('Trip Report')}
        >
          Trip Report
        </button>
        <button
          className={`ra-sub-tab ${activeReportTab === 'Billing Report' ? 'active' : ''}`}
          onClick={() => setActiveReportTab('Billing Report')}
        >
          Billing Report
        </button>
      </div>

      {/* Filters */}
      <div className="ra-filters-section">
        <div className={`ra-filter-row ${activeReportTab === 'Trip Report' ? 'trip-report-row' : ''}`}>
          <div className="ra-filter-group">
            <label className="ra-filter-label">From Date</label>
            <div className="ra-date-input-wrapper">
              <input
                type="date"
                className="ra-date-input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <span className="ra-date-icon">📅</span>
            </div>
          </div>
          <div className="ra-filter-group">
            <label className="ra-filter-label">To Date</label>
            <div className="ra-date-input-wrapper">
              <input
                type="date"
                className="ra-date-input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
              <span className="ra-date-icon">📅</span>
            </div>
          </div>
          {activeReportTab === 'Trip Report' && (
            <>
              <div className="ra-filter-group">
                <label className="ra-filter-label">Status</label>
                <select
                  className="ra-select-input"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Delayed">Delayed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="ra-filter-group">
                <label className="ra-filter-label">Vehicle</label>
                <select
                  className="ra-select-input"
                  value={vehicleFilter}
                  onChange={(e) => setVehicleFilter(e.target.value)}
                >
                  {uniqueVehicles.map(v => (<option key={v} value={v}>{v}</option>))}
                </select>
              </div>
              <div className="ra-filter-group">
                <label className="ra-filter-label">Driver</label>
                <select
                  className="ra-select-input"
                  value={driverFilter}
                  onChange={(e) => setDriverFilter(e.target.value)}
                >
                  {uniqueDrivers.map(d => (<option key={d} value={d}>{d}</option>))}
                </select>
              </div>
            </>
          )}
          {activeReportTab === 'Billing Report' && (
            <>
              <div className="ra-filter-group">
                <label className="ra-filter-label">Billing Type</label>
                <select
                  className="ra-select-input"
                  value={billingTypeFilter}
                  onChange={(e) => setBillingTypeFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="UPI">UPI</option>
                  <option value="Cash">Cash</option>
                  <option value="To Pay">To Pay</option>
                </select>
              </div>
              <div className="ra-filter-group">
                <label className="ra-filter-label">Invoice Status</label>
                <select
                  className="ra-select-input"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </>
          )}
          <div className="ra-filter-group ra-search-group">
            <label className="ra-filter-label">&nbsp;</label>
            <div className="ra-filter-actions">
              <button className="ra-generate-btn" onClick={handleGenerateReport}>
                <span>☰</span> Generate Report
              </button>
              <button className="ra-reset-btn" onClick={handleReset}>
                Reset
              </button>
          <button
            className="ra-icon-btn"
            type="button"
            title="Export as Excel"
            onClick={handleExportExcel}
          >
            📊
          </button>
          {activeReportTab === 'Billing Report' && (
            <button
              className="ra-icon-btn"
              type="button"
              title="Print Report"
              onClick={handlePrintReport}
            >
              🖨️
            </button>
          )}
            </div>
          </div>
        </div>
      </div>

      {/* Show Entries - Only show when report is generated */}
      {reportGenerated && (
        <div className="ra-show-entries" ref={showEntriesDropdownRef}>
          <div className="ra-show-left">
            <span>Show</span>
            <button
              className="ra-entries-btn"
              onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
            >
              {itemsPerPage} entries
              <span>▼</span>
            </button>
            {showEntriesDropdownOpen && (
              <div className="ra-entries-dropdown">
                {[10, 25, 50, 100].map(num => (
                  <div
                    key={num}
                    className="ra-entries-option"
                    onClick={() => {
                      setItemsPerPage(num);
                      setShowEntriesDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {num} entries
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="ra-show-search">
            <div className="ra-search-input-wrapper">
              <span className="ra-search-icon">🔍</span>
              <input
                type="text"
                className="ra-search-input"
                placeholder={activeReportTab === 'Billing Report' ? 'Search by invoice or LR number' : 'Search by trip ID or vehicle'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Table - Only show when report is generated */}
      {reportGenerated && (
        <div className="ra-table-container">
        <table className="ra-data-table">
          <thead>
            <tr>
              {getTableHeaders().map((header, idx) => (
                <th key={idx}>
                  {header}
                  {header !== 'Actions' && <span className="ra-sort-icon">▼</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {((loadingBills && activeReportTab === 'Billing Report') || (loadingTrips && activeReportTab === 'Trip Report')) ? (
              <tr>
                <td colSpan={getTableHeaders().length} className="ra-no-data">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={getTableHeaders().length} className="ra-no-data">
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => (
                <tr key={item.id}>
                  {activeReportTab === 'Trip Report' && (
                    <>
                      <td>
                        <a href="#" className="ra-link">{item.id}</a>
                      </td>
                      <td>{formatDate(item.date)}</td>
                      <td>{item.vehicleNumber}</td>
                      <td>{item.driverName}</td>
                      <td>{item.route}</td>
                      <td>{item.distanceKm}</td>
                      <td>
                        <span className={`ra-status-badge ${getStatusBadgeClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                    </>
                  )}
                  {activeReportTab === 'Billing Report' && (
                    <>
                      <td>
                        <a href="#" className="ra-link">{item.id}</a>
                      </td>
                      <td>{item.billDateFormatted || formatDate(item.billDate)}</td>
                      <td>{item.fromLocation || 'N/A'}</td>
                      <td>{item.toLocation || 'N/A'}</td>
                      <td>₹{item.totalAmount.toLocaleString('en-IN')}</td>
                      <td>
                        <span className={`ra-status-badge ${getStatusBadgeClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <div className="ra-action-icons">
                          <button className="ra-action-icon" title="Print" onClick={() => handlePrintBill(item)}>🖨️</button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      )}

      {/* Pagination - Only show when report is generated */}
      {reportGenerated && (
        <div className="ra-table-footer">
        <div className="ra-pagination-info">
          Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
          {activeReportTab === 'Billing Report' 
            ? Math.min(currentPage * itemsPerPage, billsTotalCount) 
            : activeReportTab === 'Trip Report'
            ? Math.min(currentPage * itemsPerPage, tripsTotalCount)
            : 0} of{' '}
          {activeReportTab === 'Billing Report' ? billsTotalCount : activeReportTab === 'Trip Report' ? tripsTotalCount : 0} entries
        </div>
        <div className="ra-pagination-controls">
          <button
            className="ra-pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ←
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`ra-pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="ra-pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            →
          </button>
        </div>
        </div>
      )}
    </div>
  );
};

export default AgencyReportsAnalytics;
